import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FormatListBulletedOutlined, Preview } from '@mui/icons-material';
import {
	ActionButton,
	ConfirmModal,
	EmptyState,
	FormActions,
	FormCard,
	LoadingState,
	PageHeader,
	useConfirmModal,
	useToast,
} from '@practium/ui';
import {
	getApiErrorMessage,
	useGetManagementPlans,
	useGetPublicPlans,
	useRemoveLandingPlan,
} from '@practium/features/api';
import type { FormPageMode } from '@practium/ui';
import { PlanForm } from '../../../system/components/Forms/PlanForm/PlanForm';
import type { PlanFormRef } from '../../../system/components/Forms/PlanForm/PlanForm';
import type { PaymentMethod, PlanFormData } from '../../../system/types/system.types';
import { PlanPreviewCard } from '../components/Preview/PlanPreviewCard/PlanPreviewCard';
import { usePermissions } from '@practium/features/permissions';
import { LANDING_ROUTES } from '../constants/landing.constants';
import styles from './ViewLandingPlanPage.module.css';

const isPaymentMethod = (value: string): value is PaymentMethod =>
	value === 'boleto' || value === 'credit_card' || value === 'pix' || value === 'free';

export const ViewLandingPlanPage = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { abilities } = usePermissions();
	const canEdit =
		abilities.can('update', 'landingPlans') && abilities.can('update', 'plans');
	const canDelete = abilities.can('delete', 'landingPlans');
	const routeId = searchParams.get('id') ?? '';
	const requestedPlanId = searchParams.get('planId') ?? '';
	const requestedLandingPlanId = searchParams.get('landingPlanId') ?? '';

	const publicPlansQuery = useGetPublicPlans();
	const managementPlansQuery = useGetManagementPlans({ limit: 1000 });
	const { mutateAsync: removeLandingPlan, isPending: isRemoving } = useRemoveLandingPlan();
	const landingPlanLink = publicPlansQuery.data?.find(
		item =>
			item._id === requestedLandingPlanId ||
			item._id === routeId ||
			item.plan._id === requestedPlanId
	);
	const planId = requestedPlanId || landingPlanLink?.plan._id || routeId;
	const landingPlanId = requestedLandingPlanId || landingPlanLink?._id || routeId;
	const plan = managementPlansQuery.data.find(item => item._id === planId);
	const resolvedInitialData = useMemo<PlanFormData | null>(() => {
		if (!plan) return null;

		return {
			name: plan.name,
			description: plan.description ?? '',
			paymentMethods: plan.paymentMethods.filter(isPaymentMethod),
			hierarchy: typeof plan.hierarchy === 'object' ? plan.hierarchy._id : plan.hierarchy,
			isListable: 'yes',
			price: plan.price > 0 ? (plan.price / 100).toFixed(2).replace('.', ',') : '',
			recurrentPayment: plan.recurrentPayment ? 'yes' : 'no',
			interval: plan.interval,
			intervalCount: String(plan.intervalCount ?? 1),
			gracePeriod: plan.gracePeriod == null ? '' : String(plan.gracePeriod),
			notifyUnpayment: plan.notifyUnpayment ? 'yes' : 'no',
			notifyInvoice: plan.notifyInvoice ? 'yes' : 'no',
			maxInstallments: String(plan.maxInstallments ?? 1),
			maxProps: String(plan.maxProps ?? 0),
			maxSamples: String(plan.maxSamples ?? 0),
			monthlyFloraTokens: '',
			paymentLink: '',
			advantages: plan.advantages?.length ? plan.advantages : [''],
		};
	}, [plan]);

	const [currentMode, setCurrentMode] = useState<FormPageMode>('view');
	const [isLoading, setIsLoading] = useState(false);
	const [liveData, setLiveData] = useState<PlanFormData | null>(null);
	const [liveFormData, setLiveFormData] = useState<PlanFormData | null>(null);
	const planRef = useRef<PlanFormRef>(null);
	const toast = useToast();

	const {
		confirm,
		isOpen: isConfirmOpen,
		options: confirmOptions,
		handleConfirm,
		handleCancel: handleConfirmCancel,
	} = useConfirmModal();

	useEffect(() => {
		if (resolvedInitialData && !liveData) {
			setLiveData(resolvedInitialData);
			setLiveFormData(resolvedInitialData);
		}
	}, [resolvedInitialData, liveData]);

	const handleSubmit = useCallback(() => {
		if (liveFormData?.isListable === 'no') {
			navigate(LANDING_ROUTES.CONTROL);
			return;
		}
		if (liveFormData) setLiveData(liveFormData);
		setCurrentMode('view');
	}, [liveFormData, navigate]);

	const handleFormChange = useCallback((data: PlanFormData) => {
		setLiveFormData(data);
	}, []);

	const handleCancel = useCallback(async () => {
		const revert = () => {
			setCurrentMode('view');
			setLiveData(resolvedInitialData);
			setLiveFormData(resolvedInitialData);
		};
		if (!planRef.current?.isDirty?.()) {
			revert();
			return;
		}
		const confirmed = await confirm({
			title: 'Cancelar alterações',
			message: 'Deseja descartar as alterações não salvas?',
			confirmText: 'Descartar',
			cancelText: 'Continuar editando',
			variant: 'warning',
		});
		if (confirmed) revert();
	}, [confirm, resolvedInitialData]);

	const handleDelete = useCallback(async () => {
		const confirmed = await confirm({
			title: 'Remover plano da landing page',
			message:
				'Tem certeza que deseja remover este plano da landing page? O plano continuará cadastrado no sistema.',
			confirmText: 'Remover',
			cancelText: 'Cancelar',
			variant: 'danger',
		});
		if (!confirmed) return;
		try {
			await removeLandingPlan(landingPlanId);
			toast.success('Plano removido da landing page');
			navigate(LANDING_ROUTES.CONTROL);
		} catch (error) {
			toast.error(getApiErrorMessage(error, 'Erro ao remover plano da landing page'));
		}
	}, [confirm, landingPlanId, navigate, removeLandingPlan, toast]);

	const isQueryLoading = publicPlansQuery.isLoading || managementPlansQuery.isLoading;
	const isQueryError = publicPlansQuery.isError || managementPlansQuery.isError;
	const isNotFound = !isQueryLoading && !isQueryError && (!landingPlanLink || !plan);

	if (isQueryLoading || (resolvedInitialData && !liveData)) return <LoadingState fullScreen />;

	if (isQueryError || isNotFound) {
		const error = publicPlansQuery.error ?? managementPlansQuery.error;
		return (
			<div className={styles.page}>
				<PageHeader
					title="Plano da landing page"
					subtitle="Não foi possível carregar os dados do plano"
					onBack={() => navigate(LANDING_ROUTES.CONTROL)}
				/>
				<EmptyState
					title={isNotFound ? 'Plano não encontrado na landing page' : 'Erro ao carregar plano'}
					description={
						isNotFound
							? 'O vínculo pode ter sido removido. Volte à listagem para atualizar os dados.'
							: getApiErrorMessage(error, 'Tente novamente em instantes.')
					}
				/>
				<ActionButton text="Voltar para a landing page" onClick={() => navigate(LANDING_ROUTES.CONTROL)} />
			</div>
		);
	}

	if (!liveData || !liveFormData) return <LoadingState fullScreen />;

	return (
		<div className={styles.page}>
			<PageHeader
				title={currentMode === 'view' ? 'Informações do Plano' : 'Editar Plano'}
				subtitle={
					currentMode === 'view'
						? 'Visualize os dados gerais do plano'
						: 'Edite os dados gerais do plano'
				}
				onBack={() => navigate(LANDING_ROUTES.CONTROL)}
			/>
			<div className={styles.cards}>
				<div className={styles.mainContent}>
					<FormCard
						icon={<FormatListBulletedOutlined />}
						title="Informações Gerais"
						className={styles.mainCard}
					>
						<PlanForm
							ref={planRef}
							id={planId}
							mode={currentMode}
							initialData={liveData}
							onSubmit={handleSubmit}
							onLoadingChange={setIsLoading}
							onFormChange={handleFormChange}
						/>
					</FormCard>
					<div className={styles.pageActions}>
						<FormActions
							mode={currentMode}
							onCancel={handleCancel}
							onSave={() => planRef.current?.submit()}
							isLoading={isLoading || isRemoving}
							onDelete={canDelete ? handleDelete : undefined}
							onEdit={canEdit ? () => setCurrentMode('edit') : undefined}
						/>
					</div>
				</div>
				<div className={styles.sideCard}>
					<FormCard icon={<Preview />} title="Preview">
						<div className={styles.formPreviewCard}>
							<PlanPreviewCard name={liveFormData.name} advantages={liveFormData.advantages} />
						</div>
					</FormCard>
				</div>
			</div>
			<ConfirmModal
				isOpen={isConfirmOpen}
				title={confirmOptions.title}
				message={confirmOptions.message}
				confirmText={confirmOptions.confirmText}
				cancelText={confirmOptions.cancelText}
				variant={confirmOptions.variant}
				onConfirm={handleConfirm}
				onCancel={handleConfirmCancel}
			/>
		</div>
	);
};
